import type { Application, Container, Sprite } from 'pixi.js';

export interface RendererStats {
  drawsPerFrame: number;
  sprites: number;
  baseTextures: number;
}

const DRAW_METHODS = [
  'drawElements',
  'drawArrays',
  'drawElementsInstanced',
  'drawArraysInstanced',
] as const;

// Dev-only batching probe: wraps the WebGL draw calls + renderer.render to
// measure draw calls per frame, plus a scene walk for rendered sprite count
// and distinct base textures. draws/frame staying flat as sprites/baseTextures
// climb = batching is healthy; draws/frame tracking baseTextures = batch breaks.
export function attachRendererStats(app: Application): {
  sample: () => RendererStats;
  detach: () => void;
} {
  const renderer = app.renderer as unknown as {
    gl: WebGL2RenderingContext;
    render: (...args: unknown[]) => unknown;
  };
  const gl = renderer.gl;
  let draws = 0;
  let frames = 0;
  const restores: Array<() => void> = [];

  for (const method of DRAW_METHODS) {
    const target = gl as unknown as Record<string, unknown>;
    const orig = target[method];
    if (typeof orig !== 'function') {
      continue;
    }
    target[method] = function (this: unknown, ...args: unknown[]) {
      draws++;
      return (orig as (...a: unknown[]) => unknown).apply(this, args);
    };
    restores.push(() => {
      target[method] = orig;
    });
  }

  const origRender = renderer.render.bind(renderer);
  renderer.render = (...args: unknown[]) => {
    frames++;
    return origRender(...args);
  };
  restores.push(() => {
    renderer.render = origRender;
  });

  return {
    sample: () => {
      const walked = walkSprites(app.stage);
      const stats: RendererStats = {
        drawsPerFrame: frames ? draws / frames : 0,
        sprites: walked.count,
        baseTextures: walked.baseTextures.size,
      };
      draws = 0;
      frames = 0;
      return stats;
    },
    detach: () => restores.forEach((r) => r()),
  };
}

function walkSprites(root: Container): {
  count: number;
  baseTextures: Set<number>;
} {
  let count = 0;
  const baseTextures = new Set<number>();
  const visit = (node: Container) => {
    if (!node.visible) {
      return;
    }
    const tex = (node as Sprite).texture;
    if (tex?.baseTexture) {
      count++;
      baseTextures.add(tex.baseTexture.uid);
    }
    for (const child of node.children as Container[]) {
      visit(child);
    }
  };
  visit(root);
  return { count, baseTextures };
}
